import React, { useState } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { arrayOf, shape, string, bool } from "prop-types";
import ConferenceStoryCard from "../../molecules/ConferenceStoryCard";

import "./events-listing-section.m.css";

const EventsListingSection = ({
  stories = [],
  backgroundImage,
  eventType,
  variant = "upcoming",
  className,
  isMobile,
  showEventName,
  ...props
}) => {
  const [visibleCount, setVisibleCount] = useState(variant === "previous" ? 6 : stories.length);

  if (!stories || stories.length === 0) {
    return null;
  }

  // Get the stories to display based on visibleCount
  const visibleStories = variant === "previous" ? stories.slice(0, visibleCount) : stories;
  const hasMoreStories = variant === "previous" && visibleCount < stories.length;

  const storyRows = [];
  for (let i = 0; i < visibleStories.length; i += 3) {
    storyRows.push(visibleStories.slice(i, i + 3));
  }

  const handleViewMore = () => {
    setVisibleCount(prev => Math.min(prev + 6, stories.length));
  };

  const sectionClassMap = {
    upcoming: {
      conferences: 'upcoming-conferences-section',
      workshops: 'upcoming-workshops-section',
      sessions: 'upcoming-sessions-section'
    },
    previous: {
      conferences: 'previous-conferences-section',
      workshops: 'previous-workshops-section',
      sessions: 'previous-sessions-section'
    }
  };

  const gridClassMap = {
    conferences: 'conferences-grid',
    workshops: 'workshops-grid',
    sessions: 'sessions-grid'
  };

  const rowClassMap = {
    conferences: 'conferences-row',
    workshops: 'workshops-row',
    sessions: 'sessions-row'
  };

  const sectionClass = sectionClassMap[variant][eventType];
  const gridClass = gridClassMap[eventType];
  const rowClass = rowClassMap[eventType];


  const title = variant === "upcoming" ? "Upcoming" : (eventType === "sessions" ? "Recently concluded" : "Previous");

  return (
    <section
      styleName={sectionClass}
      className={className}
      {...props}
      style={{ backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover' }}
    >
      <div styleName="section-header">
        <h2 styleName={`section-title`}>{title}</h2>
      </div>
      <div styleName={gridClass}>
        {isMobile ? (
          <div styleName="mobile-grid">
            {visibleStories.map((story, index) => (
              <ConferenceStoryCard
                key={`${variant}-mobile-${story?.id || index}`}
                story={story}
                variant={variant}
                showEventName={showEventName}
                eventType={eventType}
              />
            ))}
          </div>
        ) : (
          <div>
            {storyRows.map((row, rowIndex) => (
              <div key={`row-${rowIndex}`} styleName={rowClass}>
                {row.map((story, index) => (
                  <ConferenceStoryCard
                    key={`${variant}-${story?.id || index}`}
                    story={story}
                    variant={variant}
                    showEventName={showEventName}
                    eventType={eventType}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {hasMoreStories && (
        <div styleName="view-more-container">
          <button styleName="view-more-button" onClick={handleViewMore}>
            <div styleName="view-more-text">View more</div>
          </button>
        </div>
      )}
    </section>
  );
};

EventsListingSection.propTypes = {
  stories: arrayOf(
    shape({
      id: string,
      headline: string.isRequired,
      subheadline: string,
      "hero-image-s3-key": string,
      "hero-image-metadata": shape({}),
      slug: string.isRequired,
      attributes: shape({
        "event-date": string,
        "event-location": string
      })
    })
  ),
  backgroundImage: string,
  eventType: string.isRequired,
  variant: string,
  className: string,
  isMobile: bool
};

export default EventsListingSection;